import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { 
  LayoutDashboard, ClipboardCheck, Lightbulb, ArrowRight, CheckCircle2,
  Brain, TrendingUp, Target, Compass, Building2, FlaskConical, LineChart
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BackNavigation from "@/components/BackNavigation";

// Cinematic visual assets - 4K UHD Human-Centered
const CINEMATIC_VISUALS = {
  heroVideo: "https://videos.pexels.com/video-files/3252128/3252128-uhd_2560_1440_25fps.mp4",
  heroPoster: "https://images.pexels.com/photos/7439124/pexels-photo-7439124.jpeg?w=1920"
};

export default function JusWork() {
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.playbackRate = 0.6;
      const handleLoaded = () => setVideoLoaded(true);
      video.addEventListener('loadeddata', handleLoaded);
      return () => video.removeEventListener('loadeddata', handleLoaded);
    }
  }, []);

  // Core Functions of JusWork
  const coreFunctions = [
    {
      id: "assessment",
      icon: ClipboardCheck,
      title: "Assessment",
      description: "Measure workforce intelligence using structured diagnostics aligned with WAIT Theory dimensions.",
      capabilities: [
        "Structured Diagnostics",
        "WAIT Theory Alignment",
        "Multi-Level Assessment",
        "Validated Instruments"
      ],
      color: "#1E3A8A"
    },
    {
      id: "dashboard",
      icon: LayoutDashboard,
      title: "Intelligence Dashboard",
      description: "Access workforce intelligence scores, insights, and performance indicators in a unified view.",
      capabilities: [
        "Intelligence Scores",
        "Performance Indicators",
        "Progress Tracking",
        "Comparative Analytics"
      ],
      color: "#2563EB"
    },
    {
      id: "recommendations",
      icon: Lightbulb,
      title: "Recommendations",
      description: "Receive actionable recommendations based on assessment results and intelligence patterns.",
      capabilities: [
        "AI-Supported Insights",
        "Development Pathways",
        "Priority Actions",
        "Resource Alignment"
      ],
      color: "#991B1B"
    },
    {
      id: "opportunities",
      icon: Compass,
      title: "Opportunity Output",
      description: "Translate workforce intelligence into career pathways, service opportunities, and business directions.",
      capabilities: [
        "Career Pathways",
        "Service Opportunities",
        "Business Development",
        "Future-Ready Planning"
      ],
      color: "#3B82F6",
      badge: "Future-Ready"
    }
  ];

  // Ecosystem alignment - how JusWork fits in the Zodiac system
  const ecosystemLayers = [
    { 
      icon: Brain, 
      title: "WAIT Theory", 
      desc: "Provides the foundational framework", 
      color: "#991B1B",
      layer: "Foundation"
    },
    { 
      icon: FlaskConical, 
      title: "Zodiac Lab", 
      desc: "Develops workforce intelligence", 
      color: "#1E3A8A",
      layer: "Development"
    },
    { 
      icon: LineChart, 
      title: "Observatories", 
      desc: "Measure and analyze workforce data", 
      color: "#2563EB",
      layer: "Intelligence"
    },
    { 
      icon: LayoutDashboard, 
      title: "JusWork", 
      desc: "Delivers the platform experience", 
      color: "#3B82F6",
      layer: "Platform",
      highlight: true
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="juswork-page">
      {/* Back Navigation */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackNavigation />
      </div>

      {/* Hero - Cinematic Video Background */}
      <section className="relative overflow-hidden min-h-[70vh] flex items-center -mt-12">
        {/* Video Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
            style={{ 
              backgroundImage: `url(${CINEMATIC_VISUALS.heroPoster})`,
              opacity: videoLoaded ? 0 : 1
            }}
          />
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
            poster={CINEMATIC_VISUALS.heroPoster}
          >
            <source src={CINEMATIC_VISUALS.heroVideo} type="video/mp4" />
          </video>
          {/* Gradient Overlay - 30% opacity */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#1E3A8A]/35 via-[#1E40AF]/30 to-[#1E3A8A]/25" />
          {/* Subtle pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-medium mb-6">
              <LayoutDashboard className="w-4 h-4" />
              Digital Workforce Intelligence Platform
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white">
              JusWork Platform
            </h1>
            <p className="text-lg text-white/90 leading-relaxed mb-4">
              The digital platform that delivers workforce intelligence through assessment, analytics, and actionable recommendations.
            </p>
            <p className="text-base text-white/70 leading-relaxed mb-8 max-w-2xl mx-auto">
              JusWork is the platform layer of the Zodiac Workforce Intelligence Institute. It enables individuals, organizations, and institutions to assess, develop, and apply workforce intelligence using structured diagnostics, intelligence dashboards, and AI-supported insights.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/observatories/global/survey">
                <Button size="lg" className="bg-white text-[#1E3A8A] hover:bg-white/90 h-12 px-8" data-testid="start-assessment-btn">
                  Start Assessment
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/juswork/dashboard">
                <Button size="lg" variant="outline" className="h-12 px-8 border-white/30 text-white hover:bg-white/10" data-testid="access-dashboard-btn">
                  Access Dashboard
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Functions Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold mb-4" style={{ color: '#991B1B' }}>
              What JusWork Does
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              Four core functions that transform workforce intelligence into measurable outcomes.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {coreFunctions.map((func, index) => (
              <motion.div
                key={func.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                data-testid={`function-card-${func.id}`}
              >
                <Card className="h-full border-[#E5E7EB] bg-white hover:shadow-xl transition-all overflow-hidden">
                  <CardContent className="p-0">
                    <div 
                      className="p-6 text-white relative"
                      style={{ backgroundColor: func.color }}
                    >
                      {func.badge && (
                        <span className="absolute top-4 right-4 px-2 py-0.5 text-xs font-medium bg-white/20 rounded-full">
                          {func.badge}
                        </span>
                      )}
                      <func.icon className="w-10 h-10 mb-3" />
                      <h3 className="font-heading text-xl font-bold">
                        {func.title}
                      </h3>
                    </div>
                    <div className="p-6">
                      <p className="text-[#1E3A8A] mb-5 leading-relaxed">
                        {func.description}
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {func.capabilities.map((capability, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: func.color }} />
                            <span className="text-[#1E3A8A]">{capability}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ecosystem Alignment Section */}
      <section className="py-16 lg:py-24 bg-white border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold mb-4" style={{ color: '#991B1B' }}>
              How JusWork Fits in the Zodiac System
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              JusWork operates as the platform layer within a structured intelligence ecosystem.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ecosystemLayers.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className={`h-full border-[#E5E7EB] text-center hover:shadow-lg transition-shadow ${item.highlight ? 'ring-2 ring-[#3B82F6] ring-offset-2' : ''}`}>
                  <CardContent className="p-6">
                    <div className="text-xs font-medium text-[#1E3A8A]/50 uppercase tracking-wide mb-3">
                      {item.layer}
                    </div>
                    <div 
                      className="w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center"
                      style={{ backgroundColor: `${item.color}15` }}
                    >
                      <item.icon className="w-7 h-7" style={{ color: item.color }} />
                    </div>
                    <h3 className="font-heading text-lg font-semibold mb-2" style={{ color: item.highlight ? '#3B82F6' : '#991B1B' }}>
                      {item.title}
                    </h3>
                    <p className="text-sm text-[#1E3A8A]">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Connection Flow */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-[#F8FAFC] rounded-lg border border-[#E5E7EB]">
              <span className="text-sm text-[#1E3A8A]/70">Theory</span>
              <ArrowRight className="w-4 h-4 text-[#1E3A8A]/40" />
              <span className="text-sm text-[#1E3A8A]/70">Development</span>
              <ArrowRight className="w-4 h-4 text-[#1E3A8A]/40" />
              <span className="text-sm text-[#1E3A8A]/70">Intelligence</span>
              <ArrowRight className="w-4 h-4 text-[#1E3A8A]/40" />
              <span className="text-sm font-medium text-[#3B82F6]">Platform Experience</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Access Section */}
      <section className="py-16 lg:py-20 bg-[#1E3A8A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
              Begin Your Workforce Intelligence Assessment
            </h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              Access the JusWork platform to measure, develop, and apply workforce intelligence through structured diagnostics and actionable recommendations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/observatories/global/survey">
                <Button size="lg" className="bg-white text-[#1E3A8A] hover:bg-white/90 h-12 px-8">
                  Start Assessment
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/auth/login">
                <Button size="lg" variant="outline" className="h-12 px-8 border-white/30 text-white hover:bg-white/10">
                  Sign In to Dashboard
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
